'use client';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import InnerPageHeader from '@/components/InnerPageHeader';

export default function Home() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#F6EFED]">
      {/* Header Component */}
      <InnerPageHeader />

      {/* Hero Section */}
      <section className="relative h-[1000px] bg-[#f6efed] overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover object-center scale-125"
            style={{ objectPosition: 'center center' }}
          >
            <source src="/hero-banner.mp4" type="video/mp4" />
          </video>
          <div 
            className="absolute inset-0" 
            style={{ 
              background: 'linear-gradient(251deg, rgba(76, 62, 88, 0.00) 37.11%, rgba(76, 62, 88, 0.85) 100%), linear-gradient(0deg, rgba(76, 62, 88, 0.40) 0%, rgba(76, 62, 88, 0.40) 100%)' 
            }}
          />
        </div>
        
        <div className="relative max-w-[1920px] mx-auto h-full flex items-end pl-[215px] pr-4 sm:pr-6 lg:pr-8 pb-[200px]">
          <div className="max-w-[842px]">
            <h1 className="font-['Gotham_Rounded',sans-serif] text-[80px] font-bold leading-[96px] tracking-[-4%] text-white mb-10">
              Find Verified Women-<br />
              Only Fitness Activities<br />
              Near You
            </h1>
            
            <p className="font-['Archivo',sans-serif] text-[20px] font-normal leading-[36px] text-white mb-12 tracking-[3%]">
              Find local gyms, fitness professionals, events, deals and news in your area!<br />
              Just looking around? We can suggest something hot & happening :)
            </p>

            {/* Search Bar */}
            <div className="relative w-[527px] h-[60px] bg-white rounded-[10px] flex items-center">
              <input
                type="text"
                placeholder="Search by name, city, postcode or services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 px-4 font-['Archivo',sans-serif] text-[16px] font-normal leading-[34px] tracking-[0%] text-black placeholder:text-black/50 outline-none bg-transparent"
              />
              <button className="px-6 py-3 mr-1 bg-[#8a7296] text-white font-['Archivo',sans-serif] text-[18px] font-medium leading-normal rounded-[8px] hover:bg-[#4C3E58] transition-colors flex items-center gap-2 backdrop-blur-sm">
                Search
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Female Fitness Finder Section */}
      <section className="py-20 bg-[#f6efed]">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Gotham_Rounded',sans-serif] text-[42px] font-bold leading-[54px] tracking-[-2.1px] text-[#2e2a3a] text-center mb-16">
            Why Choose Female Fitness Finder?
          </h2>

          <div className="grid md:grid-cols-3 gap-8 max-w-[1290px] mx-auto">
            {/* Card 1 - Verified & Trusted Spaces */}
            <div className="bg-white rounded-[20px] p-8 text-center hover:bg-[#8A7296] transition-colors group">
              <div className="flex justify-center mb-6 relative">
                <Image
                  src="/Verified, Trusted Spaces.svg"
                  alt="Verified Trusted Spaces"
                  width={70}
                  height={70}
                  className="w-[70px] h-[70px] object-contain group-hover:opacity-0 transition-opacity absolute"
                />
                <Image
                  src="/verified white.svg"
                  alt="Verified White"
                  width={70}
                  height={70}
                  className="w-[70px] h-[70px] object-contain opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </div>
              <h3 className="font-['Gotham_Rounded',sans-serif] text-[22px] font-medium leading-[44px] tracking-[-1.1px] text-[#2e2a3a] mb-4 group-hover:text-white transition-colors">
                Verified, Trusted Spaces
              </h3>
              <p className="font-['Archivo',sans-serif] text-[18px] font-normal leading-[34px] text-black opacity-70 tracking-[0.54px] group-hover:text-white group-hover:opacity-100 transition-colors">
                All gyms and fitness studios are
independently reelewed and Female
Fitness Finner approved.
              </p>
            </div>

            {/* Card 2 - Use a Women-Focused Platform */}
            <div className="bg-white rounded-[20px] p-8 text-center hover:bg-[#8A7296] transition-colors group">
              <div className="flex justify-center mb-6">
                <Image
                  src="/Safe & Woman Focused.svg"
                  alt="Safe Woman Focused"
                  width={70}
                  height={70}
                  className="w-[70px] h-[70px] object-contain group-hover:brightness-0 group-hover:invert"
                />
              </div>
              <h3 className="font-['Gotham_Rounded',sans-serif] text-[22px] font-medium leading-[44px] tracking-[-1.1px] text-[#2e2a3a] mb-4 group-hover:text-white transition-colors">
                Safe & Woman Focused
              </h3>
              <p className="font-['Archivo',sans-serif] text-[18px] font-normal leading-[34px] text-black opacity-70 tracking-[0.54px] group-hover:text-white group-hover:opacity-100 transition-colors">
                Find women-only and women-led
gyms for a supportive, women first
environment.
              </p>
            </div>

            {/* Card 3 - Privacy & Comfort First */}
            <div className="bg-white rounded-[20px] p-8 text-center hover:bg-[#8A7296] transition-colors group">
              <div className="flex justify-center mb-6">
                <Image
                  src="/Privacy & Comfort First.svg"
                  alt="Privacy Comfort First"
                  width={70}
                  height={70}
                  className="w-[70px] h-[70px] object-contain group-hover:brightness-0 group-hover:invert"
                />
              </div>
              <h3 className="font-['Gotham_Rounded',sans-serif] text-[22px] font-medium leading-[44px] tracking-[-1.1px] text-[#2e2a3a] mb-4 group-hover:text-white transition-colors">
                Privacy & Comfort First 
              </h3>
              <p className="font-['Archivo',sans-serif] text-[18px] font-normal leading-[34px] text-black opacity-70 tracking-[0.54px] group-hover:text-white group-hover:opacity-100 transition-colors">
                Prioritised safety, private antes
and female-only classes strainer is
trainers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Our Directory Works Section */}
      <section className="py-20 bg-[#f6efed]">
        <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative w-[580px] h-[720px]">
              <Image
                src="/hero-image-9.jpg"
                alt="How Our Directory Works"
                fill
                className="rounded-2xl object-cover"
                sizes="580px"
              />
            </div>

            {/* Content */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <div className="w-[5px] h-[5px] bg-[#8a7296] rounded-full" />
                <h2 className="text-[20px] font-['Archivo:Medium',sans-serif] text-[#8a7296]">
                  How Our Directory Works
                </h2>
              </div>

              <h3 className="font-['Gotham_Rounded',sans-serif] text-[42px] font-bold leading-[60px] tracking-[-2.1px] text-[#2e2a3a] mb-6">
                Finding the right women<br />
                only gym or female personal<br />
                trainer is simple.
              </h3>

              <p className="font-['Archivo:Regular',sans-serif] text-[18px] leading-[34px] text-black opacity-70 mb-8">
                Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. <br></br>
                <br></br>It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged.
              </p>

              <Link
                href="/about"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#8a7296] text-white text-[18px] font-medium rounded-[10px] hover:bg-[#4C3E58] transition-colors"
              >
                About Us
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Search Local Female Gym Section */}
      <section className="py-0">
        <div className="max-w-[1920px] mx-auto px-[100px]">
          <div className="bg-white rounded-[30px] px-[60px] py-[80px]">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-[5px] h-[5px] bg-[#8a7296] rounded-full" />
            <h2 className="font-['Archivo',sans-serif] text-[20px] font-medium text-[#8a7296]">
              Search Local Female Gym
            </h2>
          </div>

          <h3 className="font-['Gotham_Rounded',sans-serif] text-[42px] font-bold leading-[60px] tracking-[-2.1px] text-[#2e2a3a] mb-6 text-center">
            Searching for your local female only gym, fitness centre,<br />
            or personal trainer has never been easier
          </h3>

          <p className="font-['Archivo',sans-serif] text-[18px] font-normal leading-[34px] text-black opacity-70 mb-12 text-center max-w-4xl mx-auto">
            Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's<br />
            standard dummy text ever since the 1500s, when an unknown printer took a galley of type.
          </p>

          {/* View Toggle Bar */}
          <div className="max-w-[1300px] mx-auto mb-4 flex items-center justify-between">
            <div className="flex gap-2">
              <button className="px-6 py-3 bg-white text-[#8a7296] text-[16px] font-medium rounded-[10px] flex items-center gap-2 border border-[#e8e1e6]">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                List
              </button>
              <button className="px-6 py-3 bg-[#8a7296] text-white text-[16px] font-medium rounded-[10px] flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                Map
              </button>
            </div>

            <div className="px-4 py-2 text-[16px] text-[#2e2a3a]">
              21 facilities found
            </div>
          </div>

          {/* Map Section */}
          <div className="relative max-w-[1300px] mx-auto h-[500px] rounded-2xl overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d317715.7118976315!2d-0.43123989999999994!3d51.528739!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sLondon%2C%20UK!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
              width="1300"
              height="500"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full"
            />
          </div>

          {/* Gym Listings Grid */}
          <div className="mt-12 grid md:grid-cols-2 gap-y-[50px] gap-x-8 px-[50px]">
            {/* Listing Card 1 */}
            <div className="bg-white rounded-[20px] overflow-hidden flex gap-[30px]">
              <div className="relative w-[250px] h-[280px] flex-shrink-0">
                <Image
                  src="/listing1.png"
                  alt="Empowered Gyms Bromley"
                  fill
                  className="object-cover rounded-l-[20px]"
                />
              </div>
              <div className="flex-1 py-6 pr-6">
                <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2e2a3a] mb-3">
                  Empowered Gyms<br />
                  Bromley Online
                </h3>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-3 py-1 bg-[#f6efed] text-[#2e2a3a] text-[12px] rounded-full">Female Only Gym</span>
                  <span className="px-3 py-1 bg-[#f6efed] text-[#2e2a3a] text-[12px] rounded-full">Women-owned</span>
                  <span className="px-3 py-1 bg-[#f6efed] text-[#2e2a3a] text-[12px] rounded-full">South London</span>
                </div>

                <p className="font-['Archivo',sans-serif] text-[14px] leading-[24px] text-[#6e6672] mb-4">
                  Empowered Gyms is a women's-only fitness centre located on Bromley's High Street, dedicated to helping women of all ages, shapes... <span className="text-[#8a7296] font-medium cursor-pointer">Read More</span>
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-[#2E2A3A]">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-[14px]">Bromley</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <button className="w-8 h-8 flex items-center justify-center text-[#8a7296] hover:bg-[#f6efed] rounded-full transition-colors">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center text-[#8a7296] hover:bg-[#f6efed] rounded-full transition-colors">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center text-[#8a7296] hover:bg-[#f6efed] rounded-full transition-colors">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10zm0-18c-4.411 0-8 3.589-8 8s3.589 8 8 8 8-3.589 8-8-3.589-8-8-8zm0 14c-3.309 0-6-2.691-6-6s2.691-6 6-6 6 2.691 6 6-2.691 6-6 6z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Listing Card 2 */}
            <div className="bg-white rounded-[20px] overflow-hidden flex gap-[30px]">
              <div className="relative w-[250px] h-[280px] flex-shrink-0">
                <Image
                  src="/listing2.png"
                  alt="Empowered Gyms Bromley"
                  fill
                  className="object-cover rounded-l-[20px]"
                />
              </div>
              <div className="flex-1 py-6 pr-6">
                <h3 className="font-['Gotham_Rounded',sans-serif] text-[20px] font-bold text-[#2e2a3a] mb-3">
                  Empowered Gyms<br />
                  Bromley Online
                </h3>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-3 py-1 bg-[#f6efed] text-[#2e2a3a] text-[12px] rounded-full">Female Only Gym</span>
                  <span className="px-3 py-1 bg-[#f6efed] text-[#2e2a3a] text-[12px] rounded-full">Women-owned</span>
                  <span className="px-3 py-1 bg-[#f6efed] text-[#2e2a3a] text-[12px] rounded-full">South London</span>
                </div>

                <p className="font-['Archivo',sans-serif] text-[14px] leading-[24px] text-[#6e6672] mb-4">
                  Empowered Gyms is a women's-only fitness centre located on Bromley's High Street, dedicated to helping women of all ages, shapes... <span className="text-[#8a7296] font-medium cursor-pointer">Read More</span>
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1 text-[#2E2A3A]">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-[14px]">Bromley</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <button className="w-8 h-8 flex items-center justify-center text-[#8a7296] hover:bg-[#f6efed] rounded-full transition-colors">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center text-[#8a7296] hover:bg-[#f6efed] rounded-full transition-colors">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center text-[#8a7296] hover:bg-[#f6efed] rounded-full transition-colors">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10zm0-18c-4.411 0-8 3.589-8 8s3.589 8 8 8 8-3.589 8-8-3.589-8-8-8zm0 14c-3.309 0-6-2.691-6-6s2.691-6 6-6 6 2.691 6 6-2.691 6-6 6z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-[50px] text-center">
            <button className="px-8 py-4 bg-[#8a7296] text-white text-[18px] font-medium rounded-[10px] hover:bg-[#4C3E58] transition-colors">
              Show More
            </button>
          </div>
          </div>
        </div>
      </section>

      {/* Business Owner & Personal Trainer CTAs */}
      <section className="pt-[50px] pb-20 bg-[#F6EFED] px-[100px]">
        <div className="max-w-[1920px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 items-end">
            {/* Business Owner CTA */}
            <div className="relative rounded-[24px] overflow-hidden h-[360px] group">
              <Image
                src="/business owner.png"
                alt="Business Owner"
                fill
                className="object-cover"
              />
              <div className="absolute inset-0 transition-all duration-300 ease-in-out" style={{ background: 'linear-gradient(0deg, rgba(76, 62, 88, 0.80) 0%, rgba(76, 62, 88, 0.80) 100%)' }} />
              <div className="absolute inset-0 bg-[#8A7296CC] opacity-0 group-hover:opacity-60 transition-opacity duration-300 ease-in-out" />
              <div className="absolute inset-0 pt-[60px] pb-[60px] pl-[60px] pr-[150px] flex flex-col justify-between">
                <h3 className="font-['Gotham_Rounded',sans-serif] text-white text-[38px] font-medium leading-[48px] tracking-[-1.9px]">
                  Are you a gym/studio owner? Get in touch to create or claim a listing.
                </h3>
                <button className="px-6 py-3 bg-[#8a7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] transition-colors flex items-center gap-2 w-fit">
                  I'm a business owner
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Personal Trainer CTA */}
            <div className="relative rounded-[24px] overflow-hidden h-[360px] group">
              <Image
                src="/Personal Trainer.png"
                alt="Personal Trainer"
                fill
                className="object-cover"
              />
              <div className="absolute inset-0 transition-all duration-300 ease-in-out" style={{ background: 'linear-gradient(0deg, rgba(76, 62, 88, 0.80) 0%, rgba(76, 62, 88, 0.80) 100%)' }} />
              <div className="absolute inset-0 bg-[#8A7296CC] opacity-0 group-hover:opacity-60 transition-opacity duration-300 ease-in-out" />
              <div className="absolute inset-0 pt-[60px] pb-[60px] pl-[60px] pr-[150px] flex flex-col justify-between">
                <h3 className="font-['Gotham_Rounded',sans-serif] text-white text-[38px] font-medium leading-[48px] tracking-[-1.9px]">
                  Are you a female personal trainer? Secure your spot now & join our listings waitlist.
                </h3>
                <button className="px-6 py-3 bg-[#8a7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] transition-colors flex items-center gap-2 w-fit">
                  I'm a Female Personal Trainer
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#F6EFED]">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-[5px] h-[5px] bg-[#8a7296] rounded-full" />
            <h2 className="font-['Archivo',sans-serif] text-[20px] font-medium text-[#8a7296]">
              FAQ's
            </h2>
          </div>

          <h3 className="font-['Gotham_Rounded',sans-serif] text-[42px] font-bold leading-[60px] tracking-[-2.1px] text-[#2e2a3a] mb-12 text-center">
            Frequently Asked Questions
          </h3>

          <div className="max-w-[1044px] mx-auto space-y-4">
            {[
              { number: '01', question: 'What is Women Only Gyms?' },
              { number: '02', question: 'Is it really only for women?' },
              { number: '03', question: 'How are gyms selected for the directory?' },
              { number: '04', question: 'Is there a fee to use the directory?' },
              { number: '05', question: 'How do I find a gym near me?' },
              { number: '06', question: 'How do I know if a gym is genuinely women-only?' },
            ].map((faq, index) => (
              <div
                key={index}
                className={`rounded-[20px] p-6 cursor-pointer transition-all ${
                  activeFaq === index ? 'bg-[#8A7296]' : 'bg-white'
                }`}
                onClick={() => toggleFaq(index)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className={`font-['Gotham_Rounded',sans-serif] text-[16px] font-medium ${
                      activeFaq === index ? 'text-white' : 'text-[#2e2a3a]'
                    }`}>{faq.number}.</span>
                    <span className={`font-['Gotham_Rounded',sans-serif] text-[16px] font-medium ${
                      activeFaq === index ? 'text-white' : 'text-[#2e2a3a]'
                    }`}>{faq.question}</span>
                  </div>
                  <span className={`text-[24px] font-light ${
                    activeFaq === index ? 'text-white' : 'text-[#2e2a3a]'
                  }`}>
                    {activeFaq === index ? '−' : '+'}
                  </span>
                </div>
                {activeFaq === index && (
                  <p className="mt-4 font-['Archivo',sans-serif] text-[16px] leading-[28px] text-white pl-9">
                    Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer.
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <button className="px-8 py-4 bg-[#8a7296] text-white font-['Archivo',sans-serif] text-[18px] font-medium rounded-[10px] hover:bg-[#4C3E58] transition-colors flex items-center gap-2 mx-auto">
              Read More FAQ's
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#60526C] text-white pt-20 pb-[20px]">
        <div className="max-w-[1490px] mx-auto px-[20px]">
          <div className="flex justify-between items-start mb-16">
            {/* Logo & Tagline */}
            <div>
              <div className="mb-4">
                <Image
                  src="/footer-logo.png"
                  alt="Female Fitness Finder"
                  width={360}
                  height={120}
                  className="object-contain"
                />
              </div>
              <p className="font-['Archivo',sans-serif] text-[20px] text-white">Finding female-led fitness, for you</p>
            </div>

            {/* Social & Contact */}
            <div className="flex flex-col items-end gap-[40px]">
              <div className="flex items-center gap-[40px]">
                <a href="#" className="text-white hover:opacity-70 transition-opacity">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="#" className="text-white hover:opacity-70 transition-opacity">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1112.324 0 6.162 6.162 0 01-12.324 0zM12 16a4 4 0 110-8 4 4 0 010 8zm4.965-10.405a1.44 1.44 0 112.881.001 1.44 1.44 0 01-2.881-.001z"/>
                  </svg>
                </a>
                <a href="#" className="text-white hover:opacity-70 transition-opacity">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                </a>
              </div>
              <div className="flex items-center gap-[40px] font-['Archivo',sans-serif] text-[16px]">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                  </svg>
                  <span>+44 750 753 5943</span>
                </div>
                <div className="w-px h-5 bg-white/30"></div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  <span>tia@femalefitnessfinder.com</span>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between mb-12">
            <Link href="/" className="font-['Archivo',sans-serif] text-[18px] text-white hover:opacity-70 transition-opacity">Home</Link>
            <Link href="/about" className="font-['Archivo',sans-serif] text-[18px] text-white hover:opacity-70 transition-opacity">About</Link>
            <Link href="/directory" className="font-['Archivo',sans-serif] text-[18px] text-white hover:opacity-70 transition-opacity">Directory</Link>
            <Link href="/for-women" className="font-['Archivo',sans-serif] text-[18px] text-white hover:opacity-70 transition-opacity">For Women</Link>
            <Link href="/contact" className="font-['Archivo',sans-serif] text-[18px] text-white hover:opacity-70 transition-opacity">Contact</Link>
          </div>

          {/* Bottom Section */}
          <div className="flex justify-between items-center pt-8 border-t border-white/20">
            <p className="font-['Archivo',sans-serif] text-[16px] text-white">
              Copyright © 2026 <span className="opacity-70">Female Fitness Finder.</span> All rights reserved.
            </p>
            <div className="flex gap-8">
              <Link href="/privacy" className="font-['Archivo',sans-serif] text-[16px] text-white hover:opacity-70 transition-opacity">Privacy Policy</Link>
              <Link href="/terms" className="font-['Archivo',sans-serif] text-[16px] text-white hover:opacity-70 transition-opacity">Terms & Conditions</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
